import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminCreateRole,
  adminDeleteRole,
  adminGetRoles,
  adminGetRolesForDropdown,
  adminUpdateRole,
  adminVerifyRoleDelete,
} from './adminApi';

export const ROLE_NAMES = {
  ADMIN: 'Admin',
  APPROVER: 'Approver',
  ENTERER: 'Enterer',
  STUDENT: 'Student',
  TUTOR: 'Tutor',
  GUEST: 'Guest',
} as const;

export const ROLE_TYPES = {
  ADMIN: 'admin',
  USER: 'user',
} as const;

export type TRoleNames = typeof ROLE_NAMES[keyof typeof ROLE_NAMES];
export type TRoleTypes = typeof ROLE_TYPES[keyof typeof ROLE_TYPES];

// Course
type TAdminCourseView = 'view';
type TAdminCourseCreate = 'create';
type TAdminCourseEdit = 'edit';
type TAdminCourseDelete = 'delete';
type TAdminCoursePublish = 'publish';
type TCourseView = 'view';

type TUserPermissionAdminCourses = Array<
  | TAdminCourseView
  | TAdminCourseCreate
  | TAdminCourseEdit
  | TAdminCourseDelete
  | TAdminCoursePublish
>;
type TUserPermissionCourses = Array<TCourseView>;
// COURSE TEMPLATE
export type TAdminCourseTemplateCreate = 'create';
export type TAdminCourseTemplateView = 'view';

type TUserPermissionAdminCourseTemplates = Array<
  TAdminCourseTemplateCreate | TAdminCourseTemplateView
>;

// SCHEDULE
type TScheduleView = 'view';
type TScheduleEdit = 'edit';
type TAdminScheduleCreate = 'create';
type TAdminScheduleView = 'view';
type TAdminScheduleEdit = 'edit';

type TUserPermissionAdminSchedule = Array<
  TAdminScheduleCreate | TAdminScheduleView | TAdminScheduleEdit
>;
type TUserPermissionSchedule = Array<TScheduleView | TScheduleEdit>;

// PROFILE
type TProfileView = 'view';
type TProfileEdit = 'edit';
type TProfileDelete = 'delete';
type TProfileQualifications = 'qualifications';
type TProfileSubjects = 'subjects';
type TProfileParent = 'parentDetails';
type TProfileTutor = 'tutorDetails';
type TProfileBank = 'bankDetails';
type TProfileExamDate = 'examDate';
type TProfileCurriculum = 'curriculum';
type TProfileNationality = 'nationality';
type TProfileEmergencyNumber = 'emergencyNumber';
type TProfileDateOfBirth = 'dateOfBirth';
type TProfilePlans = 'plans';
type TProfilePaymentHistory = 'paymentHistory';
type TProfileBillingEmail = 'billingEmail';
type TProfileTutorRates = 'tutorRates';

type TUserPermissionProfile = Array<
  | TProfileView
  | TProfileEdit
  | TProfileDelete
  | TProfileQualifications
  | TProfileSubjects
  | TProfileParent
  | TProfileTutor
  | TProfileBank
  | TProfileCurriculum
  | TProfileExamDate
  | TProfileNationality
  | TProfileEmergencyNumber
  | TProfileDateOfBirth
  | TProfilePlans
  | TProfilePaymentHistory
  | TProfileBillingEmail
  | TProfileTutorRates
>;

// Lesson History
type TAdminLessonHistoryView = 'view';
type TLessonHistoryView = 'view';

type TUserPermissionAdminLessonHistory = Array<TAdminLessonHistoryView>;
type TUserPermissionLessonHistory = Array<TLessonHistoryView>;

// SETTINGS
type TAdminSettingsView = 'view';
// FLEXIBLE PLANS
type TAdminSettingsFlexiblePlansView = 'flexiblePlansView';
type TAdminSettingsFlexiblePlansCreate = 'flexiblePlansCreate';
type TAdminSettingsFlexiblePlansDelete = 'flexiblePlansDelete';
type TAdminSettingsFlexiblePlansEdit = 'flexiblePlansEdit';
// SUBSCRIPTION PLANS
type TAdminSettingsSubscriptionPlansView = 'subscriptionPlansView';
type TAdminSettingsSubscriptionPlansCreate = 'subscriptionPlansCreate';
type TAdminSettingsSubscriptionPlansDelete = 'subscriptionPlansDelete';
type TAdminSettingsSubscriptionPlansEdit = 'subscriptionPlansEdit';
// GROUPS
type TAdminSettingsGroupsView = 'groupsView';
type TAdminSettingsGroupsCreate = 'groupsCreate';
type TAdminSettingsGroupsEdit = 'groupsEdit';
type TAdminSettingsGroupsDelete = 'groupsDelete';
// COURSE PERMISSION TEMPLATES
type TAdminSettingsCoursePermissionTemplatesView = 'coursePermissionTemplatesView';
type TAdminSettingsCoursePermissionTemplatesCreate = 'coursePermissionTemplatesCreate';
type TAdminSettingsCoursePermissionTemplatesEdit = 'coursePermissionTemplatesEdit';
type TAdminSettingsCoursePermissionTemplatesDelete = 'coursePermissionTemplatesDelete';
// STUDENT RATES
type TAdminSettingsStudentRatesView = 'studentRatesView';
type TAdminSettingsStudentRatesCreate = 'studentRatesCreate';
type TAdminSettingsStudentRatesEdit = 'studentRatesEdit';
type TAdminSettingsStudentRatesDelete = 'studentRatesDelete';
// COURSE PRICING TEMPLATES
type TAdminSettingsCoursePricingTemplatesView = 'coursePricingTemplatesView';
type TAdminSettingsCoursePricingTemplatesCreate = 'coursePricingTemplatesCreate';
type TAdminSettingsCoursePricingTemplatesEdit = 'coursePricingTemplatesEdit';
type TAdminSettingsCoursePricingTemplatesDelete = 'coursePricingTemplatesDelete';
// ROLES
type TAdminSettingsRolesView = 'rolesView';
type TAdminSettingsRolesCreate = 'rolesCreate';
type TAdminSettingsRolesEdit = 'rolesEdit';
type TAdminSettingsRolesDelete = 'rolesDelete';
// TRANSACTION LOGS
type TAdminSettingsTransactionLogsView = 'transactionLogsView';
// PAYMENT HISTORY
type TAdminSettingsPaymentHistoryView = 'paymentHistoryView';
// INVOICES
type TAdminSettingsInvoicesView = 'invoicesView';
type TAdminSettingsInvoicesCreate = 'invoicesCreate';
type TAdminSettingsInvoicesEdit = 'invoicesEdit';
// ADJUSTMENTS
type TAdminSettingsAdjustmentsCreate = 'adjustmentsCreate';
// TUTOR RATES
type TAdminSettingsTutorRatesView = 'tutorRatesView';
type TAdminSettingsTutorRatesCreate = 'tutorRatesCreate';
type TAdminSettingsTutorRatesEdit = 'tutorRatesEdit';
type TAdminSettingsTutorRatesDelete = 'tutorRatesDelete';

type TUserPermissionAdminSettings = Array<
  | TAdminSettingsView
  // FLEXIBLE PLANS
  | TAdminSettingsFlexiblePlansView
  | TAdminSettingsFlexiblePlansCreate
  | TAdminSettingsFlexiblePlansDelete
  | TAdminSettingsFlexiblePlansEdit
  // SUBSCRIPTION PLANS
  | TAdminSettingsSubscriptionPlansView
  | TAdminSettingsSubscriptionPlansCreate
  | TAdminSettingsSubscriptionPlansDelete
  | TAdminSettingsSubscriptionPlansEdit
  // GROUPS
  | TAdminSettingsGroupsView
  | TAdminSettingsGroupsCreate
  | TAdminSettingsGroupsEdit
  | TAdminSettingsGroupsDelete
  // COURSE PERMISSION TEMPLATES
  | TAdminSettingsCoursePermissionTemplatesView
  | TAdminSettingsCoursePermissionTemplatesCreate
  | TAdminSettingsCoursePermissionTemplatesEdit
  | TAdminSettingsCoursePermissionTemplatesDelete
  // STUDENT RATES
  | TAdminSettingsStudentRatesView
  | TAdminSettingsStudentRatesCreate
  | TAdminSettingsStudentRatesEdit
  | TAdminSettingsStudentRatesDelete
  // COURSE PRICING TEMPLATES
  | TAdminSettingsCoursePricingTemplatesView
  | TAdminSettingsCoursePricingTemplatesCreate
  | TAdminSettingsCoursePricingTemplatesEdit
  | TAdminSettingsCoursePricingTemplatesDelete
  // ROLES
  | TAdminSettingsRolesView
  | TAdminSettingsRolesCreate
  | TAdminSettingsRolesEdit
  | TAdminSettingsRolesDelete
  // TRANSACTION LOGS
  | TAdminSettingsTransactionLogsView
  // PAYMENT HISTORY
  | TAdminSettingsPaymentHistoryView
  // INVOICES
  | TAdminSettingsInvoicesView
  | TAdminSettingsInvoicesCreate
  | TAdminSettingsInvoicesEdit
  // ADJUSTMENTS
  | TAdminSettingsAdjustmentsCreate
  // TUTOR RATES
  | TAdminSettingsTutorRatesView
  | TAdminSettingsTutorRatesCreate
  | TAdminSettingsTutorRatesEdit
  | TAdminSettingsTutorRatesDelete
>;

// Trial Class
type TTrialClassCreate = 'create';
type TTrialClassView = 'view';
type TAdminTrialClassCreate = 'create';
type TAdminTrialClassView = 'view';
type TAdminTrialClassEdit = 'edit';

type TUserPermissionTrialClass = Array<TTrialClassCreate | TTrialClassView>;
type TUserPermissionAdminTrialClass = Array<
  TAdminTrialClassCreate | TAdminTrialClassView | TAdminTrialClassEdit
>;

// Homework
type THomeworkCreate = 'create';
type THomeworkView = 'view';
type THomeworkEdit = 'edit';
type THomeworkDelete = 'delete';
type TUserPermissionHomework = Array<
  THomeworkCreate | THomeworkView | THomeworkEdit | THomeworkDelete
>;
type TAdminHomeworkCreate = 'create';
type TAdminHomeworkView = 'view';
type TAdminHomeworkEdit = 'edit';
type TAdminHomeworkDelete = 'delete';
type TUserPermissionAdminHomework = Array<
  | TAdminHomeworkCreate
  | TAdminHomeworkView
  | TAdminHomeworkEdit
  | TAdminHomeworkDelete
>;

// Billing
type TBillingView = 'view';

type TUserPermissionBilling = Array<TBillingView>;

// Users
type TAdminUsersCreate = 'create';
type TAdminUsersView = 'view';
type TAdminUsersEdit = 'edit';
type TUserPermissionAdminUsers = Array<
  TAdminUsersCreate | TAdminUsersView | TAdminUsersEdit
>;

export const USER_PERMISSION_INDIVIDUALS: {
  // COURSE
  ADMIN_COURSE_VIEW: TAdminCourseView;
  ADMIN_COURSE_CREATE: TAdminCourseCreate;
  ADMIN_COURSE_EDIT: TAdminCourseEdit;
  ADMIN_COURSE_DELETE: TAdminCourseDelete;
  ADMIN_COURSE_PUBLISH: TAdminCoursePublish;
  COURSE_VIEW: TCourseView;
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE_CREATE: TAdminCourseTemplateCreate;
  ADMIN_COURSE_TEMPLATE_VIEW: TAdminCourseTemplateView;
  // SCHEDULE
  ADMIN_SCHEDULE_CREATE: TAdminScheduleCreate;
  ADMIN_SCHEDULE_VIEW: TAdminScheduleView;
  ADMIN_SCHEDULE_EDIT: TAdminScheduleEdit;
  SCHEDULE_VIEW: TScheduleView;
  SCHEDULE_EDIT: TScheduleEdit;
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY_VIEW: TAdminLessonHistoryView;
  LESSON_HISTORY_VIEW: TLessonHistoryView;
  // PROFILE
  PROFILE_VIEW: TProfileView;
  PROFILE_EDIT: TProfileEdit;
  PROFILE_DELETE: TProfileDelete;
  PROFILE_QUALIFICATIONS: TProfileQualifications;
  PROFILE_SUBJECTS: TProfileSubjects;
  PROFILE_PARENT: TProfileParent;
  PROFILE_TUTOR: TProfileTutor;
  PROFILE_BANK: TProfileBank;
  PROFILE_CURRICULUM: TProfileCurriculum;
  PROFILE_EXAM_DATE: TProfileExamDate;
  PROFILE_NATIONALITY: TProfileNationality;
  PROFILE_EMERGENCY_NUMBER: TProfileEmergencyNumber;
  PROFILE_PLANS: TProfilePlans;
  PROFILE_PAYMENT_HISTORY: TProfilePaymentHistory;
  PROFILE_BILLING_EMAIL: TProfileBillingEmail;
  PROFILE_TUTOR_RATES: TProfileTutorRates;
  // TRIAL CLASS
  TRIAL_CLASS_VIEW: TAdminTrialClassView;
  TRIAL_CLASS_CREATE: TAdminTrialClassCreate;
  ADMIN_TRIAL_CLASS_VIEW: TAdminTrialClassView;
  ADMIN_TRIAL_CLASS_CREATE: TAdminTrialClassCreate;
  ADMIN_TRIAL_CLASS_EDIT: TAdminTrialClassEdit;
  PROFILE_DATE_OF_BIRTH: TProfileDateOfBirth;
  // HOMEWORK
  HOMEWORK_VIEW: THomeworkView;
  HOMEWORK_CREATE: THomeworkCreate;
  HOMEWORK_EDIT: THomeworkEdit;
  HOMEWORK_DELETE: THomeworkDelete;
  ADMIN_HOMEWORK_VIEW: TAdminHomeworkView;
  ADMIN_HOMEWORK_CREATE: TAdminHomeworkCreate;
  ADMIN_HOMEWORK_EDIT: TAdminHomeworkEdit;
  ADMIN_HOMEWORK_DELETE: TAdminHomeworkDelete;
  // BILLING
  BILLING_VIEW: TBillingView;
  // SETTINGS
  ADMIN_SETTINGS_VIEW: TAdminSettingsView;
  // FLEXIBLE PLANS
  ADMIN_SETTINGS_FLEXIBLE_PLANS_CREATE: TAdminSettingsFlexiblePlansCreate;
  ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW: TAdminSettingsFlexiblePlansView;
  ADMIN_SETTINGS_FLEXIBLE_PLANS_DELETE: TAdminSettingsFlexiblePlansDelete;
  ADMIN_SETTINGS_FLEXIBLE_PLANS_EDIT: TAdminSettingsFlexiblePlansEdit;
  // SUBSCRIPTION PLANS
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE: TAdminSettingsSubscriptionPlansCreate;
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW: TAdminSettingsSubscriptionPlansView;
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT: TAdminSettingsSubscriptionPlansEdit;
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE: TAdminSettingsSubscriptionPlansDelete;
  // GROUPS
  ADMIN_SETTINGS_GROUPS_VIEW: TAdminSettingsGroupsView;
  ADMIN_SETTINGS_GROUPS_CREATE: TAdminSettingsGroupsCreate;
  ADMIN_SETTINGS_GROUPS_EDIT: TAdminSettingsGroupsEdit;
  ADMIN_SETTINGS_GROUPS_DELETE: TAdminSettingsGroupsDelete;
  // COURSE PERMISSION TEMPLATES
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW: TAdminSettingsCoursePermissionTemplatesView;
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_CREATE: TAdminSettingsCoursePermissionTemplatesCreate;
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_EDIT: TAdminSettingsCoursePermissionTemplatesEdit;
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_DELETE: TAdminSettingsCoursePermissionTemplatesDelete;
  // STUDENT RATES
  ADMIN_SETTINGS_STUDENT_RATES_VIEW: TAdminSettingsStudentRatesView;
  ADMIN_SETTINGS_STUDENT_RATES_CREATE: TAdminSettingsStudentRatesCreate;
  ADMIN_SETTINGS_STUDENT_RATES_EDIT: TAdminSettingsStudentRatesEdit;
  ADMIN_SETTINGS_STUDENT_RATES_DELETE: TAdminSettingsStudentRatesDelete;
  // COURSE PRICING TEMPLATES
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW: TAdminSettingsCoursePricingTemplatesView;
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_CREATE: TAdminSettingsCoursePricingTemplatesCreate;
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_EDIT: TAdminSettingsCoursePricingTemplatesEdit;
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_DELETE: TAdminSettingsCoursePricingTemplatesDelete;
  // ROLES
  ADMIN_SETTINGS_ROLES_VIEW: TAdminSettingsRolesView;
  ADMIN_SETTINGS_ROLES_CREATE: TAdminSettingsRolesCreate;
  ADMIN_SETTINGS_ROLES_EDIT: TAdminSettingsRolesEdit;
  ADMIN_SETTINGS_ROLES_DELETE: TAdminSettingsRolesDelete;
  // USERS
  ADMIN_USERS_CREATE: TAdminUsersCreate;
  ADMIN_USERS_VIEW: TAdminUsersView;
  ADMIN_USERS_EDIT: TAdminUsersEdit;
  // TRANSACTION LOGS
  ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW: TAdminSettingsTransactionLogsView;
  // PAYMENT HISTORY
  ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW: TAdminSettingsPaymentHistoryView;
  // INVOICES
  ADMIN_SETTINGS_INVOICES_VIEW: TAdminSettingsInvoicesView;
  ADMIN_SETTINGS_INVOICES_CREATE: TAdminSettingsInvoicesCreate;
  ADMIN_SETTINGS_INVOICES_EDIT: TAdminSettingsInvoicesEdit;
  // ADJUSTMENTS
  ADMIN_SETTINGS_ADJUSTMENTS_CREATE: TAdminSettingsAdjustmentsCreate;
  // TUTOR RATES
  ADMIN_SETTINGS_TUTOR_RATES_VIEW: TAdminSettingsTutorRatesView;
  ADMIN_SETTINGS_TUTOR_RATES_CREATE: TAdminSettingsTutorRatesCreate;
  ADMIN_SETTINGS_TUTOR_RATES_EDIT: TAdminSettingsTutorRatesEdit;
  ADMIN_SETTINGS_TUTOR_RATES_DELETE: TAdminSettingsTutorRatesDelete;
} = {
  // COURSE
  ADMIN_COURSE_VIEW: 'view',
  ADMIN_COURSE_CREATE: 'create',
  ADMIN_COURSE_EDIT: 'edit',
  ADMIN_COURSE_DELETE: 'delete',
  ADMIN_COURSE_PUBLISH: 'publish',
  COURSE_VIEW: 'view',
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE_CREATE: 'create',
  ADMIN_COURSE_TEMPLATE_VIEW: 'view',
  // SCHEDULE
  ADMIN_SCHEDULE_CREATE: 'create',
  ADMIN_SCHEDULE_VIEW: 'view',
  ADMIN_SCHEDULE_EDIT: 'edit',
  SCHEDULE_VIEW: 'view',
  SCHEDULE_EDIT: 'edit',
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY_VIEW: 'view',
  LESSON_HISTORY_VIEW: 'view',
  // PROFILE
  PROFILE_VIEW: 'view',
  PROFILE_EDIT: 'edit',
  PROFILE_DELETE: 'delete',
  PROFILE_QUALIFICATIONS: 'qualifications',
  PROFILE_SUBJECTS: 'subjects',
  PROFILE_PARENT: 'parentDetails',
  PROFILE_TUTOR: 'tutorDetails',
  PROFILE_BANK: 'bankDetails',
  PROFILE_CURRICULUM: 'curriculum',
  PROFILE_EXAM_DATE: 'examDate',
  PROFILE_NATIONALITY: 'nationality',
  PROFILE_EMERGENCY_NUMBER: 'emergencyNumber',
  PROFILE_PLANS: 'plans',
  PROFILE_PAYMENT_HISTORY: 'paymentHistory',
  PROFILE_BILLING_EMAIL: 'billingEmail',
  PROFILE_TUTOR_RATES: 'tutorRates',
  // TRIAL CLASS
  TRIAL_CLASS_VIEW: 'view',
  TRIAL_CLASS_CREATE: 'create',
  ADMIN_TRIAL_CLASS_VIEW: 'view',
  ADMIN_TRIAL_CLASS_CREATE: 'create',
  ADMIN_TRIAL_CLASS_EDIT: 'edit',
  PROFILE_DATE_OF_BIRTH: 'dateOfBirth',
  // HOMEWORK
  HOMEWORK_VIEW: 'view',
  HOMEWORK_CREATE: 'create',
  HOMEWORK_EDIT: 'edit',
  HOMEWORK_DELETE: 'delete',
  ADMIN_HOMEWORK_VIEW: 'view',
  ADMIN_HOMEWORK_CREATE: 'create',
  ADMIN_HOMEWORK_EDIT: 'edit',
  ADMIN_HOMEWORK_DELETE: 'delete',
  // BILLING
  BILLING_VIEW: 'view',
  // USERS
  ADMIN_USERS_CREATE: 'create',
  ADMIN_USERS_VIEW: 'view',
  ADMIN_USERS_EDIT: 'edit',
  // SETTINGS
  ADMIN_SETTINGS_VIEW: 'view',
  // FLEXIBLE PLANS
  ADMIN_SETTINGS_FLEXIBLE_PLANS_CREATE: 'flexiblePlansCreate',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW: 'flexiblePlansView',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_DELETE: 'flexiblePlansDelete',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_EDIT: 'flexiblePlansEdit',
  // SUBSCRIPTION PLANS
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE: 'subscriptionPlansCreate',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW: 'subscriptionPlansView',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT: 'subscriptionPlansEdit',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE: 'subscriptionPlansDelete',
  // GROUPS
  ADMIN_SETTINGS_GROUPS_VIEW: 'groupsView',
  ADMIN_SETTINGS_GROUPS_CREATE: 'groupsCreate',
  ADMIN_SETTINGS_GROUPS_EDIT: 'groupsEdit',
  ADMIN_SETTINGS_GROUPS_DELETE: 'groupsDelete',
  // COURSE PERMISSION TEMPLATES
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_CREATE:
    'coursePermissionTemplatesCreate',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW:
    'coursePermissionTemplatesView',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_EDIT:
    'coursePermissionTemplatesEdit',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_DELETE:
    'coursePermissionTemplatesDelete',
  // STUDENT RATES
  ADMIN_SETTINGS_STUDENT_RATES_VIEW: 'studentRatesView',
  ADMIN_SETTINGS_STUDENT_RATES_CREATE: 'studentRatesCreate',
  ADMIN_SETTINGS_STUDENT_RATES_EDIT: 'studentRatesEdit',
  ADMIN_SETTINGS_STUDENT_RATES_DELETE: 'studentRatesDelete',
  // COURSE PRICING TEMPLATES
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW: 'coursePricingTemplatesView',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_CREATE:
    'coursePricingTemplatesCreate',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_EDIT: 'coursePricingTemplatesEdit',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_DELETE:
    'coursePricingTemplatesDelete',
  // ROLES
  ADMIN_SETTINGS_ROLES_VIEW: 'rolesView',
  ADMIN_SETTINGS_ROLES_CREATE: 'rolesCreate',
  ADMIN_SETTINGS_ROLES_EDIT: 'rolesEdit',
  ADMIN_SETTINGS_ROLES_DELETE: 'rolesDelete',
  // TRANSACTION LOGS
  ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW: 'transactionLogsView',
  // PAYMENT HISTORY
  ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW: 'paymentHistoryView',
  // INVOICES
  ADMIN_SETTINGS_INVOICES_VIEW: 'invoicesView',
  ADMIN_SETTINGS_INVOICES_CREATE: 'invoicesCreate',
  ADMIN_SETTINGS_INVOICES_EDIT: 'invoicesEdit',
  // ADJUSTMENTS
  ADMIN_SETTINGS_ADJUSTMENTS_CREATE: 'adjustmentsCreate',
  // TUTOR RATES
  ADMIN_SETTINGS_TUTOR_RATES_VIEW: 'tutorRatesView',
  ADMIN_SETTINGS_TUTOR_RATES_CREATE: 'tutorRatesCreate',
  ADMIN_SETTINGS_TUTOR_RATES_EDIT: 'tutorRatesEdit',
  ADMIN_SETTINGS_TUTOR_RATES_DELETE: 'tutorRatesDelete',
};

export const USER_PERMISSION: {
  // COURSE
  ADMIN_COURSE: TUserPermissionAdminCourses;
  COURSE: TUserPermissionCourses;
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE: TUserPermissionAdminCourseTemplates;
  // SCHEDULE
  ADMIN_SCHEDULE: TUserPermissionAdminSchedule;
  SCHEDULE: TUserPermissionSchedule;
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY: TUserPermissionAdminLessonHistory;
  LESSON_HISTORY: TUserPermissionLessonHistory;
  // BILLING
  BILLING: TUserPermissionBilling;
  // SETTINGS
  ADMIN_SETTINGS: TUserPermissionAdminSettings;
} = {
  // COURSE
  ADMIN_COURSE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_PUBLISH,
  ],
  COURSE: [USER_PERMISSION_INDIVIDUALS.COURSE_VIEW],
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
  ],
  // SCHEDULE
  ADMIN_SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT,
  ],
  SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT,
  ],
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_VIEW],
  LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_VIEW],
  // BILLING
  BILLING: [USER_PERMISSION_INDIVIDUALS.BILLING_VIEW],
  // SETTINGS
  ADMIN_SETTINGS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_VIEW,
    // FLEXIBLE PLANS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_EDIT,
    // SUBSCRIPTION PLANS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE,
    // GROUPS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_DELETE,
    // COURSE PERMISSION TEMPLATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_DELETE,
    // STUDENT RATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_DELETE,
    // COURSE PRICING TEMPLATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_DELETE,
    // TRANSACTION LOGS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW,
    // PAYMENT HISTORY
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW,
    // INVOICES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_EDIT,
    // ADJUSTMENTS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ADJUSTMENTS_CREATE,
    // TUTOR RATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_DELETE,
  ],
};

export interface IRolePermissions {
  quiz?: Array<'view'>;
  submission?: Array<'view' | 'create' | 'edit' | 'delete'>;
  quizHistory?: Array<'view'>;
  course?: TUserPermissionCourses;
  schedule?: TUserPermissionSchedule;
  profile?: TUserPermissionProfile;
  trialClass?: TUserPermissionTrialClass;
  lessonHistory?: TUserPermissionLessonHistory;
  homework?: TUserPermissionHomework;
  billing?: TUserPermissionBilling;
  // ADMIN
  adminSubTopic?: Array<'view' | 'create' | 'edit' | 'delete'>;
  adminQuestion?: Array<'view' | 'create' | 'edit' | 'delete' | 'approve'>;
  adminQuiz?: Array<'view' | 'create' | 'edit' | 'delete' | 'publish'>;
  adminStudent?: Array<'view'>;
  adminCourse?: TUserPermissionAdminCourses;
  adminCourseTemplate?: TUserPermissionAdminCourseTemplates;
  adminSchedule?: TUserPermissionAdminSchedule;
  adminLessonHistory?: TUserPermissionAdminLessonHistory;
  adminTrialClass?: TUserPermissionAdminTrialClass;
  adminHomework?: TUserPermissionAdminHomework;
  adminUsers?: TUserPermissionAdminUsers;
  adminSettings?: TUserPermissionAdminSettings;
}

export interface IRoleNotifications {
  email: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
    payments: {
      types: string[];
      [key: string]: any;
    };
  };
  whatsapp: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
    payments: {
      types: string[];
      [key: string]: any;
    };
  };
}

export interface IRole {
  _id: string;
  name: TRoleNames;
  type: TRoleTypes;
  createdAt: string;
  permissions: IRolePermissions;
  isGuest: boolean;
  updatedAt?: string;
  notifications: IRoleNotifications;
}

export interface IRoleDeleteVerificationData {
  usersCount: number;
}

export interface IInitialRolesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminRolesListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IRole>;
  };
  adminRolesListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IRole>;
  };
  adminRoleDeleteVerificationData?: IRoleDeleteVerificationData;
}

const initialState: IInitialRolesState = {
  status: 'idle',
  // Admin
  adminRolesListData: undefined,
  adminRolesListDataForDropdown: undefined,
  adminRoleDeleteVerificationData: undefined,
};

export const RolesSlice = createSlice({
  initialState,
  name: 'rolesSlice',
  reducers: {
    resetAdminRolesListData: (state) => {
      state.adminRolesListData = undefined;
    },
    resetAdminRoleDeleteVerificationData: (state) => {
      state.adminRoleDeleteVerificationData = undefined;
    },
    resetAdminRolesListDataForDropdown: (state) => {
      state.adminRolesListDataForDropdown = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetRoles.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminRolesListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminRolesListData = newAdminRolesListData;
      })
      .addCase(adminGetRolesForDropdown.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminRolesListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminRolesListDataForDropdown = newAdminRolesListData;
      })
      .addCase(adminCreateRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminRolesListData) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          newAdminRolesListData.list.unshift(result);

          if (newAdminRolesListData.totalDocs) {
            newAdminRolesListData.totalDocs += 1;
          } else {
            newAdminRolesListData.totalDocs = 1;
          }

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminUpdateRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRolesListData &&
          state.adminRolesListData.list &&
          state.adminRolesListData.list.length
        ) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          const index = newAdminRolesListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminRolesListData.list[index] = result;
          }

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminDeleteRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRolesListData &&
          state.adminRolesListData.list &&
          state.adminRolesListData.list.length
        ) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          newAdminRolesListData.totalDocs = newAdminRolesListData.totalDocs
            ? newAdminRolesListData.totalDocs - 1
            : 0;

          newAdminRolesListData.list = newAdminRolesListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminVerifyRoleDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminRoleDeleteVerificationData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getRolesState = (state: RootState): RootState['roles'] =>
  state.roles;

export const {
  resetAdminRolesListData,
  resetAdminRoleDeleteVerificationData,
  resetAdminRolesListDataForDropdown,
} = RolesSlice.actions;

export * from './adminApi';
export default RolesSlice.reducer;
